import './CountrySelect.css';
import Select, { components, SingleValueProps, OptionProps } from 'react-select';
import { Country } from '../../models';
import { CountryFlag } from './CountryFlag';

interface CountrySelectProps {
   options: Country[];
   value: Country | undefined;
   onChange: (country: Country | null) => void;
   placeholder?: string;
}

const CustomSingleValue = ({ children, ...props }: SingleValueProps<Country>) => {
   const country = props.data;
   return (
      <components.SingleValue {...props}>
         <div className="country-select-value">
            {country.code && (
               <CountryFlag
                  isoCode={country.code}
                  size={20}
               />
            )}
            <span>{children}</span>
         </div>
      </components.SingleValue>
   );
};

const CustomOption = (props: OptionProps<Country>) => {
   const country = props.data;
   return (
      <components.Option {...props}>
         <div className="country-select-option">
            {country.code && (
               <CountryFlag
                  isoCode={country.code}
                  size={20}
               />
            )}
            <span>{country.label}</span>
         </div>
      </components.Option>
   );
};

export function CountrySelect({ options, value, onChange, placeholder = 'Select your country' }: CountrySelectProps) {
   return (
      <Select<Country>
         options={options}
         placeholder={placeholder}
         onChange={onChange}
         value={value}
         getOptionLabel={(option) => option.label}
         getOptionValue={(option) => option.code}
         components={{
            SingleValue: CustomSingleValue,
            Option: CustomOption,
         }}
         noOptionsMessage={() => 'No results found'}
         filterOption={(option, inputValue) => {
            if (!inputValue) return true;
            const searchValue = inputValue.toLowerCase();
            return option.data.label.toLowerCase().includes(searchValue) || option.data.code.toLowerCase().includes(searchValue);
         }}
         styles={{
            control: (baseStyles, state) => ({
               ...baseStyles,
               backgroundColor: 'rgb(41, 41, 41)',
               color: 'white',
               padding: '10px 8px',
               fontSize: '14px',
               borderRadius: '8px',
               boxShadow: 'none',
               border: state.isFocused ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.1)',
               '&:hover': {
                  backgroundColor: 'rgb(50, 50, 50)',
                  borderColor: 'rgba(255, 255, 255, 0.15)',
               },
            }),
            singleValue: (baseStyles) => ({
               ...baseStyles,
               color: 'white',
            }),
            input: (baseStyles) => ({
               ...baseStyles,
               color: 'white',
            }),
            placeholder: (baseStyles) => ({
               ...baseStyles,
               color: 'var(--g-text-secondary)',
            }),
            menu: (baseStyles) => ({
               ...baseStyles,
               backgroundColor: 'rgb(41, 41, 41)',
               border: '1px solid rgba(255, 255, 255, 0.1)',
               borderRadius: '8px',
               marginTop: '4px',
               zIndex: 9999,
               boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            }),
            menuList: (baseStyles) => ({
               ...baseStyles,
               backgroundColor: 'rgb(41, 41, 41)',
               padding: '0',
               maxHeight: '300px',
            }),
            option: (baseStyles, state) => ({
               ...baseStyles,
               backgroundColor: state.isSelected ? 'rgb(60, 60, 60)' : state.isFocused ? 'rgb(50, 50, 50)' : 'rgb(41, 41, 41)',
               color: state.isSelected ? 'white' : 'rgba(255, 255, 255, 0.85)',
               cursor: 'pointer',
               padding: '10px 12px',
               borderRadius: '0',
               fontSize: '14px',
               fontWeight: state.isSelected ? 600 : 400,
               borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
               '&:active': {
                  backgroundColor: 'rgb(60, 60, 60)',
               },
               '&:first-of-type': {
                  borderRadius: '8px 8px 0 0',
               },
               '&:last-of-type': {
                  borderRadius: '0 0 8px 8px',
                  borderBottom: 'none',
               },
            }),
            dropdownIndicator: (baseStyles) => ({
               ...baseStyles,
               color: 'var(--g-text-secondary)',
               '&:hover': {
                  color: 'white',
               },
            }),
            indicatorSeparator: () => ({
               display: 'none',
            }),
         }}
      />
   );
}
